<template>
	<div
		:class="['special-detail-page', { 'activity-over': activityEndDialog }]"
		style="line-height: initial"
		@click.capture="handleGxzzNavigationClick"
	>
		<div class="special-fixed">
			<DcHeader :title="columnTitleName" :isShare="true">
				<template #dropdown>
					<DropDown :show="true" />
				</template>
			</DcHeader>
		</div>

		<img v-if="details && details.topMapUrl" :src="details.topMapUrl" :alt="topicName" class="special-top-banner" />

		<div v-if="floorIndexList.length" class="floor-index-wrap" @click.stop>
			<div ref="floorIndexBarRef" class="floor-index-bar">
				<button
					v-for="item in visibleFloorIndexList"
					:key="item.layoutPlateId"
					type="button"
					:class="['floor-index-item', { active: activeFloorId === item.layoutPlateId }]"
					@click="scrollToFloor(item)"
				>
					<span>{{ getFloorIndexLabel(item) }}</span>
				</button>
				<button v-if="hasMoreFloorIndex" type="button" class="floor-index-more" @click="toggleFloorIndexPanel">
					<img src="/img/more.svg" alt="更多" :class="['floor-index-more-icon', { open: showAllFloorIndex }]" />
				</button>
			</div>
			<div class="floor-index-measure-box" aria-hidden="true">
				<button
					v-for="(item, index) in floorIndexList"
					:key="`measure-${item.layoutPlateId}`"
					:ref="element => setFloorIndexItemMeasureRef(element, index)"
					type="button"
					class="floor-index-item floor-index-item-measure"
				>
					<span>{{ getFloorIndexLabel(item) }}</span>
				</button>
				<button ref="floorIndexMoreMeasureRef" type="button" class="floor-index-more floor-index-more-measure">
					<img src="/img/more.svg" alt="" class="floor-index-more-icon" />
				</button>
			</div>

			<div v-if="showAllFloorIndex" class="floor-index-overlay" @click.stop>
				<div class="floor-index-panel">
					<button
						v-for="item in floorIndexList"
						:key="`panel-${item.layoutPlateId}`"
						type="button"
						:class="['floor-index-chip', { active: activeFloorId === item.layoutPlateId }]"
						@click="scrollToFloor(item)"
					>
						{{ getFloorIndexLabel(item) }}
					</button>
				</div>
			</div>
		</div>

		<ClientOnly>
			<van-overlay class="floor-index-mask" :show="showAllFloorIndex" :z-index="79" @click="showAllFloorIndex = false" />
		</ClientOnly>

		<div style="margin-top: 0.75rem">
			<template v-for="(item, i) in floorData" :key="item.layoutPlateId || i">
				<div v-if="showHeadlineBeforeConsult && i === headlineInsertIndex" class="headline-box">
					<GxzzSiteHeadlines class="border-radius" v-if="isGxzz && gxzzNewsList.length" :company-id="1" :news-list="gxzzNewsList" />
				</div>
				<FloorColumn class="column-mb" :item="item" :option="option" @shoppingCart="handleShoppingCart" />
			</template>
			<div v-if="showHeadlineAfterFloors" class="headline-box">
				<GxzzSiteHeadlines v-if="isGxzz && gxzzNewsList.length" :company-id="1" :news-list="gxzzNewsList" />
			</div>
		</div>

		<div v-if="dataList.length > 0" style="margin-top: 0.75rem; padding: 0 0.83rem">
			<BigImg
				:showCompanyType="false"
				style="justify-content: flex-start"
				:dataList="dataList"
				:automaticallyAddToCart="false"
				@addCart="handleShoppingCart"
			/>
		</div>

		<ClientOnly>
			<van-overlay :show="activityEndDialog" :z-index="9999" @click="activityEndDialog = false">
				<div class="activity-end-wrapper" @click.stop>
					<img style="width: 22.6rem; height: 15.5rem" src="/staticSeo/images/activityEnd.png" alt="活动已结束" />
				</div>
			</van-overlay>
		</ClientOnly>

		<div class="footer-area">
			<ClientOnly>
				<ShoppingCart ref="shoppingCartRef" />
			</ClientOnly>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { addCart } from '@/api/goods.js';
import { getHotNews } from '@/api/index.js';
import { useAnalytics } from '@/composables/useAnalytics';
import BigImg from '@/components/BigImg.vue';
import DcHeader from '@/components/DcHeader.vue';
import DropDown from '@/components/DropDown.vue';
import ShoppingCart from '@/components/core/ShoppingCart.vue';
import FloorColumn from '@/components/column/index.vue';
import GxzzSiteHeadlines from '@/pages/[companyId]/index/GxzzSiteHeadlines.vue';
import { mobileSpecialDetailPath } from '@/utils/link/mobileSpecialDetail';
import { isGxzzFromWebsiteData } from '@/utils/business/siteGxzz.js';
import { clearIsGxzzSession, getIsGxzzSession } from '@/utils/gxzz';
import { buildGxzzProductTrackingTargetId, GXZZ_TRACKING_CHANNEL, GXZZ_TRACKING_TARGET, setGxzzTrackingContext } from '@/utils/gxzzTracking';
import { showToast } from 'vant';

const HEADER_HEIGHT_REM = 3.67;
const FLOOR_INDEX_HEIGHT_REM = 3.42;

const route = useRoute();
const requestUrl = useRequestURL();
const { trackEvent } = useAnalytics();
const areaId = String(route.params?.id || route.query?.id || '').trim();
const keyWord = String(route.query?.keyWord || '').trim();

// 旧 query 专区链接统一 301 到主站 /special/{id}。
if (route.path === '/specialDetail' && areaId) {
	const specialPath = mobileSpecialDetailPath(areaId);
	if (specialPath) {
		const targetQuery = {};
		if (keyWord) targetQuery.keyWord = keyWord;
		await navigateTo(
			{
				path: specialPath,
				query: targetQuery,
			},
			{ redirectCode: 301, replace: true }
		);
	}
}

const shoppingCartRef = ref(null);
const floorIndexBarRef = ref(null);
const floorIndexMoreMeasureRef = ref(null);
const floorIndexItemMeasureRefs = ref([]);
const activeFloorId = ref('');
const showAllFloorIndex = ref(false);
const visibleFloorIndexCount = ref(0);

const activityEndDialog = ref(false);
const activityDialogInitialized = ref(false);

const { data: ssrData, error: ssrError } = await useAsyncData(`special-detail-${areaId}-${keyWord || ''}`, async () => {
	// 专区基础信息请求同时供页面数据和所属站点信息查询复用。
	const picPromise = $fetch('/api/specialDetail?type=pic', {
		method: 'POST',
		body: { areaId },
	});
	const [picRes, floorRes, productsRes, websiteRes] = await Promise.allSettled([
		picPromise,
		$fetch('/api/specialDetail?type=floor', {
			method: 'POST',
			body: { areaIds: [areaId] },
		}),
		$fetch('/api/specialDetail?type=products', {
			method: 'POST',
			body: {
				pageSize: 9999,
				pageStart: 1,
				pageNum: 1,
				areaId,
				orderFlag: 1,
				keyword: keyWord,
			},
		}),
		picPromise.then(picResult =>
			picResult?.data?.companyId
				? $fetch('/api/specialDetail', {
						query: { type: 'website', companyId: picResult.data.companyId },
					})
				: null
		),
	]);

	return {
		pic: picRes.status === 'fulfilled' ? picRes.value : null,
		floor: floorRes.status === 'fulfilled' ? floorRes.value : null,
		products: productsRes.status === 'fulfilled' ? productsRes.value : null,
		website: websiteRes.status === 'fulfilled' ? websiteRes.value : null,
	};
});

if (ssrError.value) {
	console.error('[specialDetail] SSR 数据获取失败:', ssrError.value);
}

const details = computed(() => ssrData.value?.pic?.data || { description: '', topMapUrl: '' });
/** 专区是否国械智造（对齐 PC fetchSpecialAreaBaseInfo → specialAreaInfo.isGxzz） */
const isGxzz = computed(() => isGxzzFromWebsiteData(details.value));
// 国械智造专区站内头条：随 SSR 预取，保证服务端渲染源码可见（不再依赖客户端 watch 异步填充）
const { data: gxzzHeadlineList } = await useAsyncData(`special-detail-gxzz-headline-${areaId}`, async () => {
	if (!isGxzz.value) return [];
	try {
		const res = await getHotNews({
			theTt: 1,
			theMaster: 1,
			companyId: 1,
			pageNumber: 1,
			pageNum: 1,
			pageSize: 10,
			isGxzz: 1,
		});
		return res?.code == 200 && Array.isArray(res?.data?.list) ? res.data.list : [];
	} catch (error) {
		console.error('获取国械智造专区站内头条失败:', error);
		return [];
	}
});
const floorData = computed(() => {
	const data = ssrData.value?.floor?.data;
	return Array.isArray(data) && data.length > 0 ? data : [];
});
const topicName = computed(() => String(details.value?.title || '').trim() || '专区');
const option = computed(() => ({
	backgroundColor: details.value?.backgroundColor || '',
	specialTitle: topicName.value,
	isGxzz: isGxzz.value,
	trackingChannel: GXZZ_TRACKING_CHANNEL.SPECIAL_RIGHT_NAV,
}));
const getFloorIndexLabel = item => ((item && item.floorName) || '').trim();
const floorIndexList = computed(() => floorData.value.filter(item => item?.layoutPlateId && getFloorIndexLabel(item) && item.isViewPort === 1));
const visibleFloorIndexList = computed(() => floorIndexList.value.slice(0, visibleFloorIndexCount.value));
const hasMoreFloorIndex = computed(() => floorIndexList.value.length > visibleFloorIndexCount.value);
const dataList = computed(() => ssrData.value?.products?.data?.list || []);
const gxzzNewsList = computed(() => (isGxzz.value ? gxzzHeadlineList.value.filter(item => item?.title).slice(0, 5) : []));
/**
 * 判断专区楼层接口项是否为“站内头条”占位项。
 * @param {Object} item 专区楼层接口返回的单条楼层数据
 * @return {boolean} 是否命中“站内头条”占位项
 */
const isHeadlineFloor = item => {
	const floorName = String(item?.floorName || '').trim();
	const columnTitleName = String(item?.columnTitleName || '').trim();
	return floorName === '站内头条' || columnTitleName === '站内头条';
};

/** 国械智造聚合页：站内头条位置优先跟随专区楼层接口返回顺序，未命中时再回退到历史留言楼层锚点。 */
const headlineInsertIndex = computed(() => {
	const headlineFloorIndex = floorData.value.findIndex(isHeadlineFloor);
	if (headlineFloorIndex >= 0) return headlineFloorIndex;

	const index = floorData.value.findIndex(item => Number(item?.columnType) === 9);
	return index >= 0 ? index : floorData.value.length;
});
const showHeadlineBeforeConsult = computed(() => headlineInsertIndex.value < floorData.value.length);
const showHeadlineAfterFloors = computed(() => isGxzz.value && gxzzNewsList.value.length > 0 && headlineInsertIndex.value >= floorData.value.length);
const canonicalHref = computed(() => {
	const specialPath = mobileSpecialDetailPath(areaId);
	return specialPath ? `${requestUrl.origin}${specialPath}` : '';
});

const columnTitleName = computed(() => `国械在线-${topicName.value}`);

watch(
	topicName,
	name => {
		if (activityDialogInitialized.value) return;
		activityEndDialog.value = name === '积分攻略' || name === '双11活动';
		activityDialogInitialized.value = true;
	},
	{ immediate: true }
);

useSeoMeta({
	title: () => `${topicName.value} - 国械在线`,
	ogTitle: () => `${topicName.value}专题 - 国械在线`,
	keywords: () => `${topicName.value},国械在线,医疗器械采购`,
	description: () =>
		`国械在线${topicName.value}专题，为您提供${topicName.value}相关医疗器械价格、选型、品牌、厂家、参数、售后等一站式采购信息，助您快速对比，轻松完成专业采购。`,
	ogDescription: () =>
		`国械在线${topicName.value}专题，为您提供${topicName.value}相关医疗器械价格、选型、品牌、厂家、参数、售后等一站式采购信息，助您快速对比，轻松完成专业采购。`,
	ogImage: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
});

useHead(() => ({
	link: canonicalHref.value
		? [
				{
					rel: 'canonical',
					href: canonicalHref.value,
					key: 'canonical',
				},
			]
		: [],
}));

const getRemPx = rem => {
	if (typeof window === 'undefined') return 0;
	const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
	return rem * rootFontSize;
};

const getScrollOffset = () => getRemPx(HEADER_HEIGHT_REM + FLOOR_INDEX_HEIGHT_REM);

const getElementWidth = element => {
	if (!element) return 0;
	return Math.ceil(element.getBoundingClientRect().width);
};

const getFloorIndexGapWidth = () => {
	if (!floorIndexBarRef.value || typeof window === 'undefined') return 0;
	const styles = window.getComputedStyle(floorIndexBarRef.value);
	return Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
};

const setFloorIndexItemMeasureRef = (element, index) => {
	if (!element) return;
	floorIndexItemMeasureRefs.value[index] = element;
};

const updateVisibleFloorIndexCount = () => {
	if (typeof window === 'undefined') return;

	const totalCount = floorIndexList.value.length;
	if (!totalCount) {
		visibleFloorIndexCount.value = 0;
		return;
	}

	const barWidth = Math.floor(floorIndexBarRef.value?.clientWidth || 0);
	if (!barWidth) {
		visibleFloorIndexCount.value = totalCount;
		return;
	}

	const itemWidths = floorIndexItemMeasureRefs.value
		.slice(0, totalCount)
		.map(element => getElementWidth(element))
		.filter(width => width > 0);

	if (itemWidths.length !== totalCount) {
		visibleFloorIndexCount.value = totalCount;
		return;
	}

	const gapWidth = getFloorIndexGapWidth();
	const moreWidth = getElementWidth(floorIndexMoreMeasureRef.value) || Math.ceil(getRemPx(1.33));
	let usedWidth = 0;
	let visibleCount = 0;

	for (let index = 0; index < itemWidths.length; index += 1) {
		const itemWidth = itemWidths[index];
		const nextItemWidth = itemWidth + (visibleCount > 0 ? gapWidth : 0);
		const remainingCount = totalCount - index - 1;
		const reserveMoreWidth = remainingCount > 0 ? gapWidth + moreWidth : 0;

		if (usedWidth + nextItemWidth + reserveMoreWidth <= barWidth) {
			usedWidth += nextItemWidth;
			visibleCount += 1;
			continue;
		}

		break;
	}

	visibleFloorIndexCount.value = visibleCount > 0 ? visibleCount : 1;
};

const syncVisibleFloorIndexCount = () => {
	nextTick(() => {
		updateVisibleFloorIndexCount();
	});
};

const syncActiveFloor = () => {
	if (typeof window === 'undefined' || !floorIndexList.value.length || isManualScroll || lockActiveFloorByClick) return;

	const scrollTop = window.scrollY + getScrollOffset() + 12;
	let currentFloorId = floorIndexList.value[0]?.layoutPlateId || '';

	for (const item of floorIndexList.value) {
		const target = document.getElementById(`column_${item.layoutPlateId}`);
		if (target && target.offsetTop <= scrollTop) {
			currentFloorId = item.layoutPlateId;
		}
	}

	if (activeFloorId.value !== currentFloorId) {
		activeFloorId.value = currentFloorId;
	}
};

let isManualScroll = false;
let lockActiveFloorByClick = false;
let manualScrollTimer = null;

const resumeFloorSyncByScroll = () => {
	if (!lockActiveFloorByClick) return;
	lockActiveFloorByClick = false;
};

const toggleFloorIndexPanel = () => {
	showAllFloorIndex.value = !showAllFloorIndex.value;
};

const scrollToFloor = item => {
	if (typeof window === 'undefined' || !item?.layoutPlateId) return;

	const target = document.getElementById(`column_${item.layoutPlateId}`);
	if (!target) return;

	if (isGxzz.value) {
		trackEvent({
			module: '国械智造专区',
			eventCode: 'gxzz_homepage_click',
			eventName: '国械智造专区点击',
			attrName: 'button_name',
			attrCode: '楼层',
		});
	}

	activeFloorId.value = item.layoutPlateId;
	showAllFloorIndex.value = false;
	isManualScroll = true;
	lockActiveFloorByClick = true;

	if (manualScrollTimer) {
		window.clearTimeout(manualScrollTimer);
	}

	const targetTop = target.getBoundingClientRect().top + window.scrollY - getScrollOffset();
	window.scrollTo({
		top: Math.max(targetTop, 0),
		behavior: 'smooth',
	});

	manualScrollTimer = window.setTimeout(() => {
		isManualScroll = false;
	}, 600);
};

/**
 * 捕获国械智造专区内的商品与子站跳转，使用会话数据传递来源而不改短链接。
 * @param {MouseEvent} event 页面点击事件
 */
const handleGxzzNavigationClick = event => {
	if (!import.meta.client || !isGxzz.value || !(event.target instanceof Element)) return;

	const siteTarget = event.target.closest('[data-company-id]');
	if (siteTarget?.dataset?.companyId) {
		setGxzzTrackingContext(GXZZ_TRACKING_TARGET.SUBSITE, siteTarget.dataset.companyId, GXZZ_TRACKING_CHANNEL.SPECIAL_PRODUCT_CARD_TO_SUBSITE);
		return;
	}

	const linkTarget = event.target.closest('a[href]');
	const rawHref = linkTarget?.getAttribute('href');
	if (!rawHref) return;

	try {
		const targetUrl = new URL(rawHref, window.location.origin);
		if (targetUrl.origin !== window.location.origin) return;
		// 线上移动端可能带 /cn 前缀，去除后再按业务短链识别目标。
		const targetPath = targetUrl.pathname.replace(/^\/cn(?=\/)/, '');

		const productMatch = targetPath.match(/^\/([^/]+)\/products\/([^/]+)\.html\/?$/i);
		if (productMatch) {
			const targetId = buildGxzzProductTrackingTargetId(decodeURIComponent(productMatch[1]), decodeURIComponent(productMatch[2]));
			setGxzzTrackingContext(GXZZ_TRACKING_TARGET.PRODUCT, targetId, GXZZ_TRACKING_CHANNEL.SPECIAL_PRODUCT_CARD);
			return;
		}

		const subsiteMatch = targetPath.match(/^\/([^/]+)\/?$/);
		if (subsiteMatch) {
			setGxzzTrackingContext(
				GXZZ_TRACKING_TARGET.SUBSITE,
				decodeURIComponent(subsiteMatch[1]),
				GXZZ_TRACKING_CHANNEL.SPECIAL_PRODUCT_CARD_TO_SUBSITE
			);
		}
	} catch {
		// 非法链接沿用原有跳转逻辑，不影响页面交互。
	}
};

const getGxzzRobotDebugFloors = () =>
	floorData.value
		.filter(item => [9, 12].includes(Number(item?.columnType)))
		.map(item => ({
			layoutPlateId: item?.layoutPlateId,
			columnId: item?.columnId,
			columnType: item?.columnType,
			columnTitleName: item?.columnTitleName,
			floorName: item?.floorName,
			isOnlineService: item?.isOnlineService,
			companyId: item?.companyId,
			raw: item,
		}));

const logGxzzRobotDebugData = () => {
	if (!import.meta.client) return;
	const consultFloors = getGxzzRobotDebugFloors();
	console.groupCollapsed('[gxzz robot debug] specialDetail');
	console.log('request params', { areaId, keyWord });
	console.log('pic response', ssrData.value?.pic);
	console.log('pic data for isGxzz', details.value);
	console.log('isGxzz', isGxzz.value);
	console.log('floor response', ssrData.value?.floor);
	console.table(
		consultFloors.map(({ raw, ...item }) => ({
			...item,
			shouldShowRobot: item.isOnlineService == 1,
		}))
	);
	console.log(
		'consult floors raw',
		consultFloors.map(item => item.raw)
	);
	console.groupEnd();
};

onMounted(() => {
	logGxzzRobotDebugData();
	const title = ssrData.value?.pic?.data?.title;
	if (title && !isGxzz.value) {
		trackEvent({
			companyCode: '',
			companyName: '',
			module: '专题页',
			eventCode: 'topic_page_enter',
			eventName: '进入专题页',
			attrName: 'page_name',
			attrCode: title,
		});
	}
	if (isGxzz.value) {
		trackEvent({
			companyCode: ssrData.value?.website?.data?.companyCode || details.value?.companyCode || '',
			companyName: ssrData.value?.website?.data?.companyName || ssrData.value?.website?.data?.websiteName || '',
			module: '国械智造专区',
			eventCode: 'gxzz_homepage_enter',
			eventName: '进入国械智造专区',
			attrName: 'channel_type',
			attrCode: GXZZ_TRACKING_CHANNEL.MAIN_HOME,
		});
	}

	activeFloorId.value = floorIndexList.value[0]?.layoutPlateId || '';
	lockActiveFloorByClick = false;
	nextTick(() => {
		syncActiveFloor();
		updateVisibleFloorIndexCount();
	});

	window.addEventListener('scroll', syncActiveFloor, { passive: true });
	window.addEventListener('touchmove', resumeFloorSyncByScroll, { passive: true });
	window.addEventListener('wheel', resumeFloorSyncByScroll, { passive: true });
	window.addEventListener('resize', syncVisibleFloorIndexCount);
});

onUnmounted(() => {
	clearIsGxzzSession();
	if (typeof window === 'undefined') return;
	if (manualScrollTimer) {
		window.clearTimeout(manualScrollTimer);
	}
	window.removeEventListener('scroll', syncActiveFloor);
	window.removeEventListener('touchmove', resumeFloorSyncByScroll);
	window.removeEventListener('wheel', resumeFloorSyncByScroll);
	window.removeEventListener('resize', syncVisibleFloorIndexCount);
});

watch(
	floorIndexList,
	() => {
		floorIndexItemMeasureRefs.value = [];
		syncVisibleFloorIndexCount();
	},
	{ flush: 'post' }
);

const handleShoppingCart = async (event, options) => {
	console.log('options： 409 ', options);
	trackEvent({
		companyCode: options.filialeCode,
		companyName: options.filialeName,
		module: '购物车',
		eventCode: 'add_to_cart',
		eventName: '加入购物车',
		attrCode: 'channel_type',
		attrName: '专题页',
		ext1: options.productCode,
		ext2: options.productName,
		ext3: options.displayPrice ?? options.price ?? '',
	});

	try {
		const cartParams = {
			currentCompanyId: options.companyId,
			productId: options.productId || options.productid,
			quantity: options.quantity,
			agreementPriceId: options.agreementPriceId,
			price: options.price,
			filialeCode: options.filialeCode,
		};
		const { code, message } = await addCart(cartParams);
		if (code === '200') {
			shoppingCartRef.value?.addCart(event);
			showToast({ message: '加入购物车成功', type: 'success', duration: 2000 });
		} else {
			showToast({ message: message || '加入购物车失败', type: 'fail', duration: 2000 });
		}
	} catch (error) {
		console.error('添加购物车失败', error);
	}
};
</script>

<style lang="scss" scoped>
.special-detail-page {
	padding-top: 3.67rem;
	padding-bottom: calc(1rem + env(safe-area-inset-bottom));
}

.activity-over {
	height: 100vh;
	overflow: hidden;
}

.special-fixed {
	position: fixed;
	top: 0;
	right: 0;
	width: 100%;
	z-index: 112323;
}

.special-top-banner {
	width: 100%;
	height: 8.33rem;
	display: block;
	object-fit: fill;
}

.floor-index-wrap {
	position: sticky;
	top: 3.67rem;
	z-index: 80;
	background: #ffffff;
	padding: 0.67rem 1rem 0.8rem 1rem;
	box-shadow: 0 0.08rem 0 rgba(0, 0, 0, 0.04);
}

.floor-index-bar {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 0.08rem;
	min-height: 2.75rem;
}

.floor-index-item {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	height: 2.75rem;
	min-width: 0;
	padding: 0 0.67rem;
	border: 0;
	background: transparent;
	font-family: 'Source Han Sans', sans-serif;
	font-size: 1.08rem;
	line-height: 1.52rem;
	color: #595959;
	cursor: pointer;
	white-space: nowrap;

	&::after {
		content: '';
		position: absolute;
		left: 50%;
		bottom: -0.3rem;
		width: 1.5rem;
		height: 0.56rem;
		background: transparent;
		transform: translateX(-50%);
		transition: background-color 0.2s ease;
		clip-path: path(
			'M18 1.6875C18 2.10357 17.8463 2.50497 17.5684 2.81462L17.5667 2.81652Q15.9081 4.66388 13.7448 5.68344Q11.4817 6.75 9 6.75Q6.51831 6.75 4.25524 5.68344Q2.09078 4.66335 0.431608 2.81462C0.153709 2.50497 0 2.10357 0 1.6875C0 0.75552 0.755519 1.60933e-06 1.6875 1.60933e-06C2.16671 1.60933e-06 2.62332 0.203743 2.94339 0.560385L2.94453 0.561656Q4.14755 1.90165 5.69405 2.6305Q7.27377 3.375 9 3.375Q10.7262 3.375 12.3059 2.6305Q13.8525 1.90165 15.0555 0.561655L15.0566 0.560386C15.3767 0.203742 15.8333 0 16.3125 0C17.2445 0 18 0.755519 18 1.6875Z'
		);
	}

	span {
		display: block;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&.active {
		font-size: 1.33rem;
		font-weight: 500;
		line-height: 1.87rem;
		color: #455bd9;

		&::after {
			background: #455bd9;
		}
	}
}

.floor-index-more {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 1.33rem;
	margin-left: auto;
	width: 1.33rem;
	height: 2.75rem;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.floor-index-more-icon {
	display: block;
	width: 1.33rem;
	height: 1.33rem;
	object-fit: contain;
	object-position: center;
	transition: transform 0.2s ease;

	&.open {
		transform: rotate(180deg);
	}
}

.floor-index-measure-box {
	position: fixed;
	left: -9999px;
	top: -9999px;
	display: flex;
	align-items: center;
	gap: 0.08rem;
	visibility: hidden;
	pointer-events: none;
	white-space: nowrap;
}

.floor-index-item-measure {
	flex: 0 0 auto;
}

.floor-index-more-measure {
	margin-left: 0;
}

.floor-index-panel {
	position: relative;
	display: flex;
	flex-wrap: wrap;
	gap: 0.67rem;
	padding: 0.58rem 0.67rem 0.67rem;
	background: #ffffff;
	border-radius: 0.33rem;
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08);

	&::before {
		content: '';
		position: absolute;
		top: -0.25rem;
		right: 0.33rem;
		width: 0.67rem;
		height: 0.67rem;
		background: #ffffff;
		transform: rotate(45deg);
		border-radius: 0.06rem;
	}
}

.floor-index-chip {
	min-width: 4.67rem;
	max-width: calc(25% - 0.51rem);
	height: 2.58rem;
	padding: 0 0.67rem;
	border: 0;
	border-radius: 0.33rem;
	background: #f5f5f5;
	font-family: 'Source Han Sans', sans-serif;
	font-size: 1rem;
	font-weight: 400;
	line-height: 1.58rem;
	color: #333333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	cursor: pointer;

	&.active {
		background: #edf0ff;
		color: #455bd9;
		font-weight: 500;
	}
}

.floor-index-mask {
	background: rgba(0, 0, 0, 0.24);
}

.floor-index-overlay {
	position: absolute;
	top: calc(100% + 0.33rem);
	left: 0;
	right: 0;
	z-index: 81;
}

.column-mb {
	margin-bottom: 0.75rem;

	&:last-child {
		margin-bottom: 0;
	}
}

.activity-end-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
}

.headline-box {
	padding: 0 0.83rem;
	margin-bottom: 0.75rem;
}

.border-radius {
	border-radius: 0.67rem;
}

.footer-area {
	position: fixed;
	bottom: 0;
	width: 100%;
	background: #f3f4f6;
	z-index: 100;
}
</style>
